<template>
  <div>
    <div class="row">
      <div class="col-md-6">
        <h2>Survey User Administration</h2>
      </div>
    </div>
    <div v-if="mode === 1 && selectedFilter > -1" class="row">
      <div class="col-md-6">
        <b>Filter by: </b>
        <el-select
          :id="`${_uid}-filter-options`"
          class="select-primary"
          size="small"
          placeholder="Select Filter"
          v-model="selectedFilter">
          <el-option
            v-for="option in filterOptions"
            class="select-primary"
            :value="option.value"
            :label="option.label"
            :key="option.label">
          </el-option>
        </el-select>
      </div>
    </div>
    <div class="row justify-content-md-center">
      <div class="col-md-12">
        <card>
          <div slot="header"><h4 class="card-title">{{title}} Users</h4></div>
          <div class="row">
            <div class="col-sm-12">
              <el-table :data="users[selectedFilter]" row-key="id" 
                :default-sort="{ prop: 'username', order: 'ascending' }"
                :row-class-name="tableRowClassName"
                default-expand-all>
                <el-table-column v-if="mode === 1 && selectedFilter === 2" prop="surveyName" label="Survey Name" sortable></el-table-column>
                <el-table-column v-else-if="mode === 1 && selectedFilter === 3" prop="surveyUrlId" label="Survey Instance" sortable></el-table-column>
                <el-table-column v-else prop="username" label="Username" sortable></el-table-column>
                <el-table-column v-if="mode === 1 && selectedFilter === 2" prop="username" label="Username" sortable></el-table-column>
                <el-table-column v-else-if="mode === 1 && selectedFilter !== 2" prop="surveyName" label="Survey Name" sortable></el-table-column>
                <el-table-column v-if="mode === 1 && selectedFilter === 1" prop="surveyUrlId" label="Survey Instance" sortable></el-table-column>
                <el-table-column v-else-if="mode === 1 && selectedFilter === 3" prop="username" label="Username" sortable></el-table-column>
                <el-table-column prop="email" label="Email" sortable></el-table-column>
                <el-table-column label="Email Confirmed">
                  <template scope="scope">
                    {{ scope.row.emailConfirmed !== null ? (!!scope.row.emailConfirmed ? 'Yes' : 'No') : '' }}
                  </template>
                </el-table-column>
                <el-table-column prop="role" label="Access Level" sortable></el-table-column>
                <el-table-column align="right" label="Actions">
                  <template scope="scope">
                    <base-button v-if="scope.row.dataType > 0" class="btn-link" type="secondary" size="sm" icon>
                      <span class="sr-only">Edit User</span>
                      <i class="tim-icons icon-pencil"></i>
                    </base-button>
                    <base-button v-if="scope.row.dataType > 0" @click="onDelete(scope.row)" class="btn-link" type="danger" size="sm" icon>
                      <span class="sr-only">Delete User</span>
                      <i class="tim-icons icon-simple-remove"></i>
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </card>
      </div>
    </div>
  </div>
</template>
<script>
import { Table, TableColumn } from 'element-ui';
import { mapGetters } from 'vuex';
import { getSurveyAdminUsers, deleteSurveyAdminUser, getRolesAsync } from 'src/modules/adminActions';
import BaseButton from 'src/components/BaseButton.vue';
import swal from 'sweetalert2';
import { isNullOrEmpty, copyObject } from '../../modules/utils';
import { Select, Option } from 'element-ui';

export default {
  components: {
    BaseButton,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Option.name]: Option,
    [Select.name]: Select,
  },
  data() {
    return {
      title: "Survey",
      modes: { username: 1, surveyId: 2, surveyUrlId: 3 },
      dataTypes: { header: 0, parent: 1, child: 2 },
      userData: [],
      users: [],
      roles: [],
      routeInfo: { name: "", path: "" },
      filterOptions: [
        { type: "username", label: "Username", value: 1 },
        { type: "surveyId", label: "Survey Name", value: 2 },
        { type: "surveyUrlId", label: "Survey Instance", value: 3 },
      ],
      selectedFilter: -1,
      uninitialized: -1,
    }
  },
  computed: {
    mode() {
      return this.$route.params.surveyId && this.$route.params.surveyUrlId ? this.modes.surveyUrlId
        : this.$route.params.surveyId ? this.modes.surveyId
        : this.modes.username;
    },
    /**
     * The default index to the 'users' array for the type of data view -- filter by Username (modes.username), Survey Name (modes.surveyId), 
     * or Survey Instance (modes.surveyUrlId) -- depending on the mode we're managing the adding/deleting of users into/from a survey. 
     * If we're in mode surveyId or surveyUrlId, only index 1 (modes.username) is used for storing their view data because the 'Filter by'
     * pulldown selection box is not available in those modes. It is necessary to only use index 1 in the surveyId or surveyUrlId modes so
     * we don't accidentally mix the two view types -- tree view in modes.username and normal (non-tree) view in modes.surveyId and modes.surveyUrlId.
     */
    filterMode() {
      return this.mode === this.modes.username ? (this.selectedFilter !== this.uninitialized ? this.selectedFilter : this.modes.username) : this.modes.username;
    },
    multiChildrenSurveys() {
      if (this.selectedFilter === this.uninitialized || isNullOrEmpty(this.users[this.selectedFilter])) {
        return 0;
      }

      const found = this.users[this.selectedFilter].filter(x => x.hasOwnProperty("children") && x.children.length > 0);
      return found.length;
    },
    ...mapGetters({
      getSurveyById: 'getSurveyById',
      getOrgSurveyByUrlId: 'getOrgSurveyByUrlId',
      getOrgSurveyById: 'getOrgSurveyById',
    })
  },
  methods: {
    async getRoles() {
      this.roles = await getRolesAsync();
    },
    getUsers() {
      getSurveyAdminUsers()
        .then(response => {
          this.formatUsers(response.data);
          this.updateUserList();
        });
    },
    tableRowClassName({row, rowIndex}) {
      let className = "";
      if (row.dataType === this.dataTypes.header) {
        className = "row-highlight";
      }
      else if (this.multiChildrenSurveys > 0 && row.dataType === this.dataTypes.parent) {
        className = "row-highlight-secondary";
      }
      
      return className;
    },
    formatUsers(data) {
      let count = 0;
      this.users = [];
      this.userData = [];
      if (data.length > 0) {
        data.forEach(u => {
          count++;
          const numberOfChildren = u.surveys && u.surveys.length > 0 ? u.surveys.length : 0;
          if (numberOfChildren === 0) {
            return;
          }

          const parentUsername = u.username;
          const oneChild = numberOfChildren === 1 ? u.surveys[0] : null;
          let surveyName = oneChild && oneChild.hspSurveyId ? this.getSurveyById(oneChild.hspSurveyId).surveyName : "";
          let orgSurvey = oneChild && oneChild.orgSurveyId ? this.getOrgSurveyById(oneChild.orgSurveyId) : null;

          let user = {
            id: count,
            orgSurveyAdminUserId: oneChild ? oneChild.id : null,
            parentUsername: parentUsername,
            username: u.username,
            email: u.email,
            emailConfirmed: u.emailConfirmed,
            roleId: u.roles[0].roleId,
            role: u.roles[0].name,
            hspSurveyId: oneChild ? oneChild.hspSurveyId : null,
            surveyName: surveyName,
            orgSurveyId: oneChild ? oneChild.orgSurveyId : null,
            surveyUrlId: orgSurvey ? orgSurvey.surveyUrlId : "",
            dataType: oneChild ? this.dataTypes.parent : this.dataTypes.header,
            children: [],
          };

          if (numberOfChildren > 1) {
            let childCount = 0;
            u.surveys.forEach(s => {
              childCount++;
              orgSurvey = s.orgSurveyId ? this.getOrgSurveyById(s.orgSurveyId) : null;
              const surveyUrlId = orgSurvey ? orgSurvey.surveyUrlId : "";
              surveyName = s.hspSurveyId ? this.getSurveyById(s.hspSurveyId).surveyName : "";

              let survey = {
                id: count.toString() + childCount.toString(),
                orgSurveyAdminUserId: s.id,
                parentUsername: parentUsername,
                username: "ID" + s.id,
                email: "",
                emailConfirmed: null,
                roleId: "",
                role: "",
                hspSurveyId: s.hspSurveyId,
                surveyName: surveyName,
                orgSurveyId: s.orgSurveyId,
                surveyUrlId: surveyUrlId,
                dataType: this.dataTypes.child,
              };

              user.children.push(survey);
            });
          }

          this.userData.push(user);
        });
      }
    },
    getDataByFilterType(filterType) {
      filterType = filterType || this.selectedFilter;
      if (filterType <= this.modes.username) {
        return [...this.userData];
      }

      const searchName = filterType === this.modes.surveyId ? "surveyName" : "surveyUrlId";
      let filteredData = [];
      let surveyIds = this.userData.flatMap(x => {
        if (x.dataType === this.dataTypes.header) {
          return x.children.map(y => y[searchName]);
        }
        return x[searchName];
      }).filter(x => !isNullOrEmpty(x));
      if (surveyIds.length > 0) {
        let uniqueIds = [...new Set(surveyIds)];
        let count = 0;
        uniqueIds.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        uniqueIds.forEach(x => {
          count++;
          let udCount = 0;
          let surveyIdUsers = this.userData.flatMap(y => {
            udCount++;
            let foundItems = [];
            if (y.dataType === this.dataTypes.header) {
              let founds = y.children.filter(z => z[searchName] === x);
              if (founds.length > 0) {
                let childCount = 0;
                founds.forEach(f => {
                  childCount++;
                  foundItems.push({
                    id: f.id,
                    parentUsername: f.parentUsername,
                    username: y.username,
                    email: y.email,
                    emailConfirmed: y.emailConfirmed,
                    roleId: y.roleId,
                    role: y.role,
                    hspSurveyId: f.hspSurveyId,
                    surveyName: "",
                    orgSurveyId: f.orgSurveyId,
                    surveyUrlId: "",
                    orgSurveyAdminUserId: f.orgSurveyAdminUserId,
                    dataType: this.dataTypes.child,
                  });
                });
              }
            }
            else if (y[searchName] === x) {
              foundItems.push({...y});
            }

            return foundItems;
          });

          if (surveyIdUsers.length === 1) {
            let user = {...surveyIdUsers[0]};
            user.surveyName = searchName === "surveyName" ? x
              : this.getSurveyById(this.getOrgSurveyById(user.orgSurveyId).hspSurveyId).surveyName,
            user.surveyUrlId = searchName === "surveyUrlId" ? x : "";
            user.dataType = this.dataTypes.parent;
            user.children = [];
            filteredData.push(user);
          }
          else if (surveyIdUsers.length > 0) {
            // If there were single items ('parent') found for this survey ID during the search for all data belonging to this survey ID
            // in the filter method this.userData.flatMap(), convert them into children.
            surveyIdUsers.filter(x => x.dataType !== this.dataTypes.child).forEach(x => {
              x.dataType = this.dataTypes.child;
              x.surveyName = "";
              x.surveyUrlId = "";
            });

            const firstChild = surveyIdUsers[0];
            filteredData.push({
              id: x,
              parentUsername: null,
              username: null,
              email: null,
              emailConfirmed: null,
              roleId: null,
              role: null,
              hspSurveyId: firstChild.hspSurveyId,
              surveyName: searchName === "surveyName" ? x 
                : this.getSurveyById(this.getOrgSurveyById(firstChild.orgSurveyId).hspSurveyId).surveyName,
              orgSurveyId: firstChild.orgSurveyId,
              surveyUrlId: searchName === "surveyUrlId" ? x : "",
              orgSurveyAdminUserId: null,
              dataType: this.dataTypes.header,
              children: [...surveyIdUsers].sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase())),
            });
          }
        });
      }

      return filteredData;
    },
    updateUserList(filterType, forceUpdate = false) {
      filterType = filterType || this.uninitialized;
      if (!forceUpdate && filterType !== this.uninitialized && !isNullOrEmpty(this.users[filterType])) {
        return;
      }

      let users = [];
      if (this.userData.length > 0) {
        if (this.mode === this.modes.surveyId) {
          this.userData.forEach(x => {
            let user = {...x};
            user.dataType = this.dataTypes.parent;
            if (!isNullOrEmpty(user.children)) {
              const children = user.children.filter(y => y.hspSurveyId === this.$route.params.surveyId);
              if (children.length > 0) {
                user.children = [...children];
                // If there's only one child, put the child as the parent and delete the children list.
                if (user.children.length === 1) {
                  const child = user.children[0];
                  user.orgSurveyAdminUserId = child.orgSurveyAdminUserId;
                  user.hspSurveyId = child.hspSurveyId;
                  user.surveyName = child.surveyName;
                  user.orgSurveyId = child.orgSurveyId;
                  user.surveyUrlId = child.surveyUrlId;
                  user.children = [];
                }
                else {
                  user.dataType = this.dataTypes.header;
                }
                users.push(user);
              }
            }
            else if (user.hspSurveyId === this.$route.params.surveyId) {
              users.push(user);
            }
          });
        }
        else if (this.mode === this.modes.surveyUrlId) {
          const orgSurvey = this.getOrgSurveyByUrlId(this.$route.params.surveyUrlId);
          this.userData.forEach(x => {
            let user = {...x};
            user.dataType = this.dataTypes.parent;
            if (!isNullOrEmpty(user.children)) {
              const children = user.children.filter(y => y.orgSurveyId === orgSurvey.orgSurveyId);
              if (children.length > 0) {
                user.children = [...children];
                // If there's only one child, put the child as the parent and delete the children list.
                if (user.children.length === 1) {
                  const child = user.children[0];
                  user.orgSurveyAdminUserId = child.orgSurveyAdminUserId;
                  user.hspSurveyId = child.hspSurveyId;
                  user.surveyName = child.surveyName;
                  user.orgSurveyId = child.orgSurveyId;
                  user.surveyUrlId = child.surveyUrlId;
                  user.children = [];
                }
                else {
                  user.dataType = this.dataTypes.header;
                }
                users.push(user);
              }
            }
            else if (user.orgSurveyId === orgSurvey.orgSurveyId) {
              users.push(user);
            }
          });
        }
        else {
          users = this.getDataByFilterType(filterType);
        }

        let usersResult = copyObject(users);
        let targetFilterType = filterType;
        if (filterType === this.uninitialized && this.selectedFilter === this.uninitialized) {
          this.selectedFilter = this.filterMode;
          targetFilterType = this.selectedFilter;
        }

        if (targetFilterType === this.modes.username) {
          usersResult.forEach(x => {
            if (x.dataType === this.dataTypes.parent) {
              if (x.orgSurveyId && !x.hspSurveyId) {
                x.surveyName = this.getSurveyById(this.getOrgSurveyById(x.orgSurveyId).hspSurveyId).surveyName;
              }
              return;
            }

            for (let child of x.children) {
              if (child.orgSurveyId && !child.hspSurveyId) {
                child.surveyName = this.getSurveyById(this.getOrgSurveyById(child.orgSurveyId).hspSurveyId).surveyName;
              }
            }
          });
        }
        this.users[targetFilterType] = usersResult;
      }
    },
    updateTitle() {
      let text = "Survey";
      if (this.$route.params.surveyId) {
        text = this.getSurveyById(this.$route.params.surveyId).surveyName;
        if (this.$route.params.surveyUrlId) {
          text += " (" + this.$route.params.surveyUrlId + ")";
        }
      }
      else if (this.$route.params.surveyUrlId) {
        text = "Instance " + this.$route.params.surveyUrlId;
      }

      this.title = text;
    },
    sortUsers() {
      if (this.users.length === 0) {
        return;
      }

      this.users.forEach(x => {
        if (x.children) {
          x.children.sort((a, b) => a.surveyName.toLowerCase().localeCompare(b.surveyName.toLowerCase()));
        }
      });
    },
    isNumber(value) {
      return !isNaN(value);
    },
    deleteFromUsers(data, destination) {
      let retVal = { success: false, itemRemoved: null, removedIndex: -1 };
      if (isNullOrEmpty(destination)) {
        retVal.errors = ["object to remove a data point is null or undefined"];
        return retVal;
      }
      
      let index = -1;
      let users = destination;
      let found = users.find(x => x.dataType !== this.dataTypes.header && x.orgSurveyAdminUserId === data.orgSurveyAdminUserId);
      if (found) {
        index = users.indexOf(found);
        if (index > -1) {
          retVal.success = true;
          retVal.removedIndex = index;
          retVal.itemRemoved = copyObject(users[index]);
          users.splice(index, 1);

          return retVal;
        }
      }

      // Check the children
      let count = 0;
      let promoteToParent = null;
      let idNumbers = users.flatMap(x => x.id).filter(x => this.isNumber(x)).map(x => Math.round(x));
      const maxId = idNumbers.reduce((max, x) => (x > max ? x : max), 0);
      for (let item of users) {
        count++;
        if (isNullOrEmpty(item.children)) {
          continue;
        }

        found = item.children.find(x => x.orgSurveyAdminUserId === data.orgSurveyAdminUserId);
        if (!found) {
          continue;
        }

        index = item.children.indexOf(found);
        if (index > -1) {
          retVal.success = true;
          retVal.removedIndex = index;
          retVal.itemRemoved = copyObject(item.children[index]);
          item.children.splice(index, 1);          

          // If there's only one child left, move it to a parent data type
          if (item.children.length === 1) {
            const oc = item.children[0];
            const ocHspSurvey = !isNullOrEmpty(oc.hspSurveyId) ? this.getSurveyById(oc.hspSurveyId) : null;
            const ocOrgSurvey = !isNullOrEmpty(oc.orgSurveyId) ? this.getOrgSurveyById(oc.orgSurveyId) : null;
            promoteToParent = { index: count - 1, data: copyObject(oc) };
            let promo = promoteToParent.data;
            promo.dataType = this.dataTypes.parent;
            promo.id = (maxId + 1).toString(); //item.id;// (maxId + 1).toString();
            if (!promo.surveyName.length) {
              promo.surveyName = !isNullOrEmpty(item.surveyName) ? item.surveyName
                : ocOrgSurvey ? ocOrgSurvey.surveyName 
                : ocHspSurvey ? ocHspSurvey.surveyName
                : oc.surveyName;
            }
            if (!promo.surveyUrlId.length) {
              promo.surveyUrlId = !isNullOrEmpty(item.surveyUrlId) ? item.surveyUrlId
                : ocOrgSurvey ? ocOrgSurvey.surveyUrlId 
                : oc.surveyUrlId;
            }
            if (!isNullOrEmpty(item.email)) {
              promo.email = item.email;
            }
            if (!isNullOrEmpty(item.emailConfirmed)) {
              promo.emailConfirmed = item.emailConfirmed;
            }
            if (oc.parentUsername.length) {
              promo.username = oc.parentUsername;
            }
            if (!isNullOrEmpty(item.role)) {
              promo.role = item.role;
            }
            if (!isNullOrEmpty(item.roleId)) {
              promo.roleId = item.roleId;
            }
          }

          break;
        }
      }

      if (promoteToParent) {
        users.splice(promoteToParent.index, 1, promoteToParent.data);
      }

      return retVal;
    },
    onDelete(row) {
      const tmpFilter = this.selectedFilter;
      const username = row.parentUsername;
      const name = row.username;
      let surveyNameDeleted = 
        !row.surveyName && !row.surveyUrlId ? (
          row.orgSurveyId ? `survey instance "${this.getOrgSurveyById(row.orgSurveyId).surveyUrlId}"`
          : `survey "${this.getSurveyById(row.hspSurveyId).surveyName}"`
        )
        : (row.surveyUrlId.length > 0 ? `survey instance "${row.surveyUrlId}"` : `survey "${row.surveyName}"`);
      let confirmText = 
        row.dataType === this.dataTypes.parent || this.selectedFilter !== this.modes.username ? 'Are you sure you want to delete the user <b>' + username + "</b> from the " + surveyNameDeleted + "?"
        : 'Are you sure you want to delete the assigned survey "' + name + '" from user <b>' + username + "</b>?";
      swal.fire({
        title: 'Delete Survey User',
        html: confirmText,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes',
      }).then((result) => {
        if (result.value) {
          let payload = {
            id: row.orgSurveyAdminUserId,
            // refreshData: true,
          };

          // If we're not retrieving new data, to trigger a refresh of the table display after deleting an item from the existing data, 
          // we must set to 'uninitialized' and the property 'this.selectedFilter' will be set to the correct filter type later to produce the trigger.
          // The table will not refresh if the property 'this.selectedFilter' stays the same value before and after deleting existing data.
          if (!payload.hasOwnProperty("refreshData")) {
            this.selectedFilter = this.uninitialized;
          }

          deleteSurveyAdminUser(payload)
          .then(response => {
            let result = response.data;
            let errorMessage = '';

            if (result.errors && result.errors.length) {
              result.errors.forEach(error => {
                errorMessage += `${error}<br>`;
              });
              swal('Delete Error', errorMessage, 'error');
            }
            else {
              let delResult = { success: true };
              if (result.surveyUsers) {
                this.formatUsers(result.surveyUsers);
                this.updateUserList(tmpFilter, true);
                delResult.itemRemoved = {
                  surveyName: row.surveyName,
                  surveyUrlId: row.surveyUrlId.length === 0 && row.orgSurveyId ? this.getOrgSurveyById(row.orgSurveyId).surveyUrlId : row.surveyUrlId,
                };
              }
              else {
                // Remove from the main data and recreate the the filter type view data.
                delResult = this.deleteFromUsers(row, this.userData);
                Object.entries(this.modes).forEach(([key, value]) => {
                  if (isNullOrEmpty(this.users[value])) {
                    return;
                  }

                  this.deleteFromUsers(row, this.users[value]);
                });

                // Restore to previous value to trigger a table refresh
                this.selectedFilter = tmpFilter;
              }

              if (delResult.success) {
                swal('', 'User <b>' + username + '</b> removed from ' + surveyNameDeleted, 'success');
              }
              else if (delResult.errors && delResult.errors.length) {
                const outputConsole = true;
                const carRtn = outputConsole ? "\r\n" : "<br>";
                delResult.errors.forEach(error => {
                  errorMessage += `${error}${carRtn}`;
                });

                if (outputConsole) {
                  console.log(`Internal Error:\r\n${errorMessage}`);
                }
                else {
                  swal('Internal Error', errorMessage, 'error');
                }
              }
            }
          });
        }
      })
    },
    initialize() {
      this.selectedFilter = this.uninitialized;
      this.routeInfo = { name: this.$route.name, path: this.$route.path };
      this.updateTitle();
      this.getUsers();
    },
  },
  watch: {
    // Watch for routing into the same instance of the component. Reinitialize the users when the route has changed.
    $route: {
      handler() {
        if (this.routeInfo.name !== this.$route.name) {
          this.initialize();
        }
      },
      deep: true
    },
    selectedFilter: function(newValue, oldValue) {
      if (newValue !== this.uninitialized) {
        this.updateUserList(newValue);
      }
    },
  },
  async created() {
    await this.getRoles();
    this.initialize();
  }
};
</script>
